// Wave 11 home rows (CONTRACTS.md "Wave 11: catalogue rows"): the catalogue resolves rule rows, seasons and the
// new-game boost, so the site only orders and renders them. An accounts without `rows` falls back to `lists`.
// Pure functions here; the pages draw them.

import { artUrl, gameById, type Catalogue, type Game, type Row } from "./catalogue.ts";
import { categoryForRow } from "./zones/category/data.ts";

/** The Open Haunt row's id. Its presence in `rows` is what "in season" means: accounts owns the clock. */
export const HALLOWEEN_ROW = "halloween";
/** The Updated badge lasts this long after a confirmed update (and the boost's "New" as long after a listing). */
export const UPDATED_SECS = 7 * 86400;

export interface HomeRow {
  id: string;
  title: string;
  style: string;
  games: Game[];
  boosted: Set<string>;
}

const resolve = (cat: Catalogue, ids: string[]) => ids.map((id) => gameById(cat, id)).filter((g): g is Game => !!g);

/** A row the client has to fill from the player's own data. The website has no library, so it drops them. */
export function isPersonal(r: Pick<Row, "personal" | "rule">): boolean {
  return r.personal === true || typeof r.rule?.personal === "string";
}

/**
 * The rows to draw, in order: the catalogue's `rows` without personal ones or empty ones. During the season the
 * Open Haunt row moves up to the first row after the hero(es). Without `rows` (an older accounts), `lists`.
 */
export function homeRows(cat: Catalogue): HomeRow[] {
  if (!Array.isArray(cat.rows)) return cat.lists.map((l) => ({ id: l.id, title: l.title, style: l.style, games: resolve(cat, l.games), boosted: new Set<string>() }));
  const out: HomeRow[] = cat.rows
    .filter((r) => r && typeof r.id === "string" && Array.isArray(r.games) && !isPersonal(r))
    .map((r) => ({ id: r.id, title: r.title, style: r.style, games: resolve(cat, r.games), boosted: new Set(Array.isArray(r.boosted) ? r.boosted : []) }))
    .filter((r) => r.games.length > 0)
    // the hero keeps the moderators' pin order; every other row leads with real art (wave 12)
    .map((r) => (r.style === "hero" ? r : { ...r, games: artFirst(r.games) }));
  const i = out.findIndex((r) => r.id === HALLOWEEN_ROW);
  if (i > 0) {
    const [haunt] = out.splice(i, 1);
    const at = out.findIndex((r) => r.style !== "hero");
    out.splice(at < 0 ? out.length : at, 0, haunt!);
  }
  return out;
}

/** 0 = real key art, 1 = a typographic cover (w12-art's `artKind: "type"`), 2 = no image at all. */
export function artRank(g: Pick<Game, "art" | "cover">): number {
  if (!coverUrl(g)) return 2;
  return (g as { artKind?: unknown }).artKind === "type" ? 1 : 0;
}

/** Games with real art first, then typographic covers, then none; the row's own order within each (a stable sort). */
export function artFirst<T extends Pick<Game, "art" | "cover">>(games: T[]): T[] {
  return games.map((g, i) => ({ g, i, r: artRank(g) })).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.g);
}

/** True while the catalogue shows the Open Haunt row (23 Oct – 10 Nov by accounts' clock). */
export function inSeason(cat: Pick<Catalogue, "rows">): boolean {
  return !!cat.rows?.some((r) => r.id === HALLOWEEN_ROW && Array.isArray(r.games) && r.games.length > 0);
}

/** The Open Haunt row's games, or [] out of season. */
export function hauntGames(cat: Catalogue): Game[] {
  const r = cat.rows?.find((x) => x.id === HALLOWEEN_ROW);
  return r ? resolve(cat, r.games) : [];
}

/**
 * Where a row's title links: the Open Haunt page for the Halloween row, a category's world for a Discover row that has
 * one ("Driving and racing" opens Racing, wave 13), the browse page for the rest.
 */
export function rowHref(id: string): string {
  if (id === HALLOWEEN_ROW) return "/haunt";
  const c = categoryForRow(id);
  return c?.zone ? `/z/${c.zone}` : `/browse?row=${encodeURIComponent(id)}`;
}

/** Confirmed update in the last 7 days (`versionUpdatedAt`, never `updatedAt`, which only means the listing changed). */
export function isUpdated(g: Pick<Game, "versionUpdatedAt">, nowSec = Date.now() / 1000): boolean {
  const t = g.versionUpdatedAt;
  return typeof t === "number" && t > 0 && t <= nowSec + 300 && nowSec - t < UPDATED_SECS;
}

/** "v1.4.0" from "1.4.0" or "v1.4.0" (never "vv"); other labels as they are; null for none (fingerprint updates). */
export function versionText(label: string | null | undefined): string | null {
  const l = typeof label === "string" ? label.trim() : "";
  if (!l) return null;
  return /^\d/.test(l) ? `v${l}` : l;
}

/** "Updated: v1.4.0", or "Updated" when the source has no version. */
export function updatedText(g: Pick<Game, "versionLabel">): string {
  const v = versionText(g.versionLabel);
  return v ? `Updated: ${v}` : "Updated";
}

/** A card's badge: Updated (a confirmed update in 7 days), else New for a game in the row through the boost. */
export function cardBadge(g: Pick<Game, "id" | "versionUpdatedAt">, boosted?: Set<string>, nowSec = Date.now() / 1000): "Updated" | "New" | null {
  if (isUpdated(g, nowSec)) return "Updated";
  return boosted?.has(g.id) ? "New" : null;
}

/** A `cover` we load: https, or a path on our own origin. */
export function safeCover(c: unknown): string | null {
  if (typeof c !== "string" || c.length > 500) return null;
  if (/^https:\/\/[^\s"'()\\]+$/.test(c)) return c;
  return /^\/(?!\/)[^\s"'()\\]*$/.test(c) ? c : null;
}

/** The cover: our art at /art/<key> (banger.sh/art in production), else the game's `cover`, else nothing. */
export function coverUrl(g: Pick<Game, "art" | "cover">, variant: "full" | "thumb" | "blur" = "thumb"): string | null {
  return artUrl(g.art, variant) ?? safeCover(g.cover);
}

/**
 * A trailer we play: only files we host (this origin, or banger.sh and its subdomains), never someone else's server
 * (WAVE11-LAUNCH.md §8: host it ourselves, don't hotlink).
 */
export function trailerSrc(url: unknown, origin: string): string | null {
  if (typeof url !== "string" || !url) return null;
  let u: URL;
  try {
    u = new URL(url, origin);
  } catch {
    return null;
  }
  const ours = u.origin === origin || (u.protocol === "https:" && (u.hostname === "banger.sh" || u.hostname.endsWith(".banger.sh")));
  return ours ? u.href : null;
}
